import dayjs from 'dayjs'
import { filter, find, isNumber, keyBy, map } from 'lodash'
import { useMemo } from 'react'

import useManagedOrgTree from '~/data/api/manager/accessors/organizations'
import { useNowMemo } from '~/data/time/hooks'
import { utilizationIntervalForRange } from '~/data/time/transformations'
import type { TimeRange } from '~/data/time/types'
import type { Tuple } from '~/data/types'
import type { SiteLoadManagementOption } from '~/data/types/site'
import useLogger from '~/debug/logger'
import { useCurrentOrgId } from '~/store/slices/currentOrg'

import type {
  ChargeManagementConfiguration,
  DepotEventModel,
  GetChargerEventsArgs,
  SiteEnergyConfiguration
} from '../client'
import {
  useGetBulkSiteEnergyConfigurationsQuery,
  useGetChargerEventsQuery,
  useGetDepotEventsByDepotIdQuery,
  useGetDepotEventsQuery,
  useGetScheduledDepotPowerQuery,
  useGetSiteEnergyConfigurationsQuery,
  useGetSmartPricesQuery,
  useGetWeatherQuery
} from '../client'
import type { DepotModel, GetDepotsArgs } from '../client/chargers'
import { useGetDepotUtilizationQuery, useGetDepotsQuery } from '../client/chargers'
import { useManagedSites } from '../manager'
import type { UsePollingOptions, UseQueryOptions } from './polling'
import { usePolling } from './polling'
import type { MaybeDateRange, ValidatedTime } from './types'
import { useSiteHomeSiteFilterCallback } from './useHomeSiteFiltering'
import { useLanguage } from './users'
import { combineSkip, useValidatedDateRange } from './utils'

/******************************************************************************
 * GET /depots/{depotId}
 ******************************************************************************/
type UseSiteInput = {
  args?: { id?: string }
  options?: UsePollingOptions
}

export function useSite({ args, options }: UseSiteInput = {}) {
  const indexedDbSites = useManagedSites({ options })

  return useMemo(() => {
    const { sites, ...rest } = indexedDbSites
    return { ...rest, site: sites.find((s) => s.depotId === args?.id) }
  }, [indexedDbSites, args?.id])
}

/******************************************************************************
 * GET /api/charger-events
 ******************************************************************************/
type ChargerEventFilters = Pick<
  GetChargerEventsArgs,
  'siteId' | 'chargerId' | 'connectorId' | 'vehicleId' | 'targetType' | 'ocppTag' | 'eventType'
>

type UseChargerEventsArgs = Omit<
  Partial<GetChargerEventsArgs>,
  keyof ChargerEventFilters | 'startsBefore' | 'endsAfter'
>

export function usePolledChargerEvents(
  filters?: ChargerEventFilters,
  dateRange?: MaybeDateRange,
  args?: UseChargerEventsArgs,
  options?: UsePollingOptions
) {
  const { from, to } = useValidatedDateRange(dateRange)
  const result = usePolling(
    useGetChargerEventsQuery,
    { ...filters, startsBefore: to.time, endsAfter: from.time, page: 0, count: 1000, ...args },
    combineSkip(!to.valid || !from.valid, options)
  )

  return { chargerEvents: result.data?.items, ...result }
}

export function useChargerEvents(
  filters?: ChargerEventFilters,
  dateRange?: MaybeDateRange,
  args?: UseChargerEventsArgs,
  options?: UsePollingOptions
) {
  return usePolledChargerEvents(filters, dateRange, args, { ...options, intervalSeconds: 0 })
}

type EventParams = {
  chargerId?: string
  siteId?: string
}

/**
 * Retrieves the number of charger events for the next day and week.
 * Either chargerId or siteId must be provided, but not both.
 */
export function useChargerEventCounts({ chargerId, siteId }: EventParams) {
  const logger = useLogger('useChargerEventCounts')
  if (chargerId && siteId) {
    logger.warn('Both chargerId and siteId provided')
  }

  const now = useNowMemo()
  const timeRange = { from: now, to: now.add(1, 'week') }

  const { data: chargerEvents } = useGetChargerEventsQuery({
    chargerId,
    siteId,
    page: 0,
    count: 1000,
    targetType: 'CHARGER',
    startsAfter: timeRange.from.toISOString(),
    startsBefore: timeRange.to.toISOString()
  })

  return useMemo(() => {
    const cutoff = now.add(1, 'day')
    const nextDay = (chargerEvents?.items ?? []).filter((e) => dayjs(e.scheduledStart).isBefore(cutoff)).length
    const nextWeek = chargerEvents?.totalItems ?? 0

    return { nextDay, nextWeek }
  }, [now, chargerEvents])
}

/******************************************************************************
 * GET /depots/events
 ******************************************************************************/
export function useActiveSiteEvents() {
  const now = useNowMemo()
  const result = usePolling(useGetDepotEventsQuery, {
    from: now.subtract(1, 'minute').toISOString(),
    to: now.toISOString()
  })

  return { siteEvents: result.data, ...result }
}

/******************************************************************************
 * GET /depots/{depotId}/events
 ******************************************************************************/
const siteEventBoundaryFn = (event: DepotEventModel): Tuple<string> => [event.eventStartDate, event.eventEndDate]
export function usePolledSiteEventsBySite(
  { siteId, timeRange }: { siteId?: string; timeRange: TimeRange },
  options?: UsePollingOptions
) {
  const { from, to } = useValidatedDateRange(timeRange)
  const { data, ...result } = usePolling(
    useGetDepotEventsByDepotIdQuery,
    { depotId: siteId ?? '', from: from.time, to: to.time },
    combineSkip(!siteId || !to.valid || !from.valid, options)
  )

  const categories = useCategorizedEvents(data, siteEventBoundaryFn)
  return { siteEvents: data, categories, ...result }
}

export function useActiveSiteEventsForSite(siteId?: string) {
  const now = useNowMemo()
  return usePolledSiteEventsBySite({ siteId, timeRange: { from: now.subtract(1, 'minute'), to: now } })
}

/******************************************************************************
 * GET /depots
 ******************************************************************************/
type UseSitesInput = {
  args?: GetDepotsArgs
  options?: UsePollingOptions
}

export function usePolledSites({ args, options }: UseSitesInput = {}) {
  const { useCache, ...pollingOptions } = options ?? {}

  // split into two pages
  const resultPage1 = usePolling(useGetDepotsQuery, { count: 1000, page: 0, ...args }, pollingOptions)
  const resultPage2 = usePolling(useGetDepotsQuery, { count: 1000, page: 1, ...args }, pollingOptions)

  // Combine once all the data is returned
  const combinedSites = useMemo(() => {
    if (resultPage1.isSuccess && resultPage2.isSuccess) {
      return [...(resultPage1.data.items ?? []), ...(resultPage2.data.items ?? [])]
    }
    return null
  }, [resultPage1, resultPage2])

  // Combine cached data from currentData (last successful result for each page)
  const cachedSites = useMemo(() => {
    const page1Items = resultPage1.currentData?.items ?? []
    const page2Items = resultPage2.currentData?.items ?? []
    return [...page1Items, ...page2Items]
  }, [resultPage1.currentData, resultPage2.currentData])

  const pages = [resultPage1, resultPage2]
  const isLoading = pages.some((page) => page.isLoading)
  const isFetching = pages.some((page) => page.isFetching)

  const filter = useSiteHomeSiteFilterCallback()

  // When useCache is true, prefer cached data.
  // Cached reference will update as fresh data loads.
  const effectiveSites = useCache && cachedSites.length > 0 ? cachedSites : combinedSites

  const sites = useMemo(() => {
    return effectiveSites?.filter(filter)
  }, [effectiveSites, filter])

  // When useCache is true, only show loading if there's no cached data
  const effectiveIsLoading = useCache ? isLoading && cachedSites.length === 0 : isLoading

  return { sites, isLoading: effectiveIsLoading, isFetching }
}

type UseSitesArgs = Omit<GetDepotsArgs, 'fleetId' | 'organizationId'>

export function useSites({ args, options }: UseSitesInput = {}) {
  return useManagedSites({
    filters: { orgId: args?.organizationId?.[0] },
    options
  })
}

type UseOrgSitesInput = {
  orgId: string
  args?: UseSitesArgs
  options?: UsePollingOptions
}

export function useOrgSites({ orgId, args, options }: UseOrgSitesInput) {
  return useSites({
    args: { organizationId: [orgId], ...args },
    options: combineSkip(!orgId, options)
  })
}

type UseCurrentOrgSitesInput = {
  args?: UseSitesArgs
  options?: UsePollingOptions
}

export function useCurrentOrgSites({ args, options }: UseCurrentOrgSitesInput = {}) {
  return useOrgSites({ orgId: useCurrentOrgId(), args, options })
}

/**
 * The sites an organization owns or contains, rather than every site it can
 * see.
 *
 * `useOrgSites` answers visibility, and deliberately admits orgs and sites
 * reached through a share. Use this instead wherever the question is
 * containment — assigning a device to a site, for example, where a shared
 * site would place the device outside the organization that holds it.
 *
 * While the org tree is still loading the list is empty rather than
 * unfiltered, so a caller never briefly sees sites it would then reject.
 */
export function useOwnedOrgSites({ orgId, args, options }: UseOrgSitesInput) {
  const result = useOrgSites({ orgId, args, options })
  const { orgTree, isLoading: isOrgTreeLoading, isFromApi } = useManagedOrgTree()

  const sites = useMemo(() => {
    if (!orgId) return result.sites
    // The tree hydrates from IndexedDB before the customers query answers, and
    // keeps that copy if the fetch then fails. Either way it can be a complete
    // hierarchy that is nonetheless out of date, and filtering against it would
    // offer a site whose organization has since been reparented. Wait for this
    // session's own answer rather than for loading to stop, which it also does
    // on failure. A background refresh is unaffected: it already has one.
    if (!isFromApi) return []
    const owned = orgTree.realHierarchyIds(orgId)
    return result.sites.filter((site) => owned.has(site.fleetId ?? ''))
  }, [result.sites, orgId, orgTree, isFromApi])

  return { ...result, sites, isLoading: result.isLoading || isOrgTreeLoading }
}

export function useOrgSitesMap({ args, options }: UseSitesInput = {}) {
  const { isLoading, sites } = useSites({ args, options })
  const orgSites = useMemo(() => keyBy(sites, 'depotId'), [sites])
  return { orgSites, isLoading }
}

/**
 * Returns a map of site IDs to SiteModel objects for the current organization
 * and its descendants. This should be used during cloaking scenarios to ensure
 * only sites within the cloaked organization hierarchy are returned.
 */
export function useCurrentOrgSitesMap(args?: UseSitesArgs, options?: UsePollingOptions) {
  const currentOrgId = useCurrentOrgId()
  return useOrgSitesMap({ args: { organizationId: [currentOrgId], ...args }, options: combineSkip(!currentOrgId, options) })
}

/******************************************************************************
 * GET /sites/{siteId}/energy-configurations
 ******************************************************************************/
export function useLatestSiteEnergyConfiguration(siteId: string = '') {
  const result = useGetSiteEnergyConfigurationsQuery({ siteId, latest: true }, { skip: !siteId })
  return useMemo(() => parseSiteEnergyConfiguration(result.data?.at(0)), [result.data])
}

export function useSiteHasLoadManagementEnabled(siteId: string = '', values?: SiteLoadManagementOption[]) {
  const { chargeManagementConfiguration } = useLatestSiteEnergyConfiguration(siteId) ?? {}
  return siteHasLoadManagementEnabled(chargeManagementConfiguration, values)
}

function siteHasLoadManagementEnabled(
  { loadManagement }: ChargeManagementConfiguration = {},
  values: SiteLoadManagementOption[] = ['FIXED', 'DYNAMIC']
) {
  return Boolean(loadManagement && values.includes(loadManagement))
}

/**
 * Parses a site energy configuration to ensure that the site limit is only returned if load
 * management is enabled. This should be ensured by the API, but as of 5/30/24 it is not.
 */
export function parseSiteEnergyConfiguration(config?: SiteEnergyConfiguration) {
  const cmConfig = config?.chargeManagementConfiguration
  return {
    ...config,
    chargeManagementConfiguration: {
      ...config?.chargeManagementConfiguration,
      siteLimit: siteHasLoadManagementEnabled(cmConfig) ? cmConfig?.siteLimit : undefined
    }
  }
}

export type ParsedSiteEnergyConfiguration = ReturnType<typeof parseSiteEnergyConfiguration>

/**
 * Returns the active site limit for a site. This loads all active site events and the latest site
 * energy configuration. Returns the site event's curtailed site limit if it exists, otherwise the
 * site limit from the energy configuration.
 */
export function useActiveSiteLimit(siteId?: string) {
  const { chargeManagementConfiguration } = useLatestSiteEnergyConfiguration(siteId) ?? {}
  const { categories } = useActiveSiteEventsForSite(siteId)

  return useMemo(() => {
    const siteEvent = find(categories.unfinished, { depotId: siteId })
    const { curtailedSiteLimit } = siteEvent?.schedule.intervals[0] ?? {}
    return curtailedSiteLimit ?? chargeManagementConfiguration?.siteLimit
  }, [chargeManagementConfiguration, categories, siteId])
}

/******************************************************************************
 * GET /sites/energy-configurations
 ******************************************************************************/
export function useBulkSiteEnergyConfigurationsForOrg(orgId?: string, options?: UseQueryOptions) {
  const result = useGetBulkSiteEnergyConfigurationsQuery(
    { organizationId: orgId ?? '' },
    combineSkip(!orgId, options)
  )
  const parsed = useMemo(() => map(result.data, parseSiteEnergyConfiguration), [result.data])
  return { siteEnergyConfigurations: parsed, ...result }
}

export function useBulkSiteEnergyConfigurations(options?: UseQueryOptions) {
  return useBulkSiteEnergyConfigurationsForOrg(useCurrentOrgId(), options)
}

export function useBulkSiteEnergyConfigurationsMap(options?: UseQueryOptions) {
  const result = useBulkSiteEnergyConfigurations(options)
  const siteEnergyConfigurationsMap = useMemo(() => keyBy(result.siteEnergyConfigurations, 'siteId'), [result])
  return { siteEnergyConfigurationsMap, ...result }
}

export function useSitesWithDynamicLoadManagement() {
  const { siteEnergyConfigurationsMap } = useBulkSiteEnergyConfigurationsMap()
  return useMemo(() => {
    const withDynamicLoadMgmt = filter(siteEnergyConfigurationsMap, (config) => {
      return config.chargeManagementConfiguration.loadManagement === 'DYNAMIC'
    })

    return new Set(map(withDynamicLoadMgmt, 'siteId'))
  }, [siteEnergyConfigurationsMap])
}

export function useSiteWeather(site?: DepotModel) {
  const { latitude, longitude } = site ?? {}
  const language = useLanguage()
  return useGetWeatherQuery(
    { lat: String(latitude ?? 0), lon: String(longitude ?? 0), lang: language },
    { skip: !isNumber(latitude) || !isNumber(longitude) }
  )
}

/******************************************************************************
 * GET /depots/{siteId}/smart-prices
 ******************************************************************************/
/**
 * Polls for site smart price data. Default polling interval is 5 seconds.
 */
export function usePolledSmartPrices(
  { site, timeRange }: { site: DepotModel | undefined; timeRange: TimeRange },
  options?: UsePollingOptions
) {
  const { from } = useValidatedDateRange(timeRange)
  const { to } = useForecastRange(timeRange)

  // Guard optional `site` before skip — callers may pass undefined while the depot is loading.
  const hasTariff = Boolean(site?.tariff || site?.rateCardId)
  const siteId = site?.depotId
  const { data, ...result } = usePolling(
    useGetSmartPricesQuery,
    { depotId: siteId ?? '', to: to.time, from: from.time },
    combineSkip(!to.valid || !from.valid || !siteId || !hasTariff, { intervalSeconds: 5, ...options })
  )

  return { smartPrices: data, ...result }
}

/******************************************************************************
 * GET /depots/{siteId}/power-forecast
 ******************************************************************************/
/**
 * Polls for site scheduled power data. Default polling interval is 5 seconds.
 */
export function usePolledScheduledPower(
  { siteId, timeRange }: { siteId?: string; timeRange: TimeRange },
  options?: UsePollingOptions
) {
  const { from, to } = useForecastRange(timeRange)

  const { data, ...result } = usePolling(
    useGetScheduledDepotPowerQuery,
    { depotId: siteId ?? '', to: to.time, from: from.time },
    combineSkip(!to.valid || !siteId, { intervalSeconds: 5, ...options })
  )

  return { scheduledPower: data, ...result }
}

/******************************************************************************
 * GET /depots/{siteId}/utilization
 ******************************************************************************/
/**
 * Polls for site utilization data. Default polling interval is 60 seconds (the data is not updated
 * more frequently than that).
 */
export function usePolledUtilization(
  { siteId, timeRange }: { siteId?: string; timeRange: TimeRange },
  options?: UsePollingOptions
) {
  const { from, to } = useValidatedDateRange(timeRange)
  const { data, ...result } = usePolling(
    useGetDepotUtilizationQuery,
    { depotId: siteId ?? '', to: to.time, from: from.time, intervalPeriod: utilizationIntervalForRange(timeRange) },
    combineSkip(!to.valid || !from.valid || !siteId, { intervalSeconds: 60, ...options })
  )

  return { utilization: data, ...result }
}

/**
 * Retrieves depot energy utilization with 15-minute intervals and energy measures.
 * Non-polling. Returns totalEnergyImport/Export arrays.
 */
export function useDepotEnergyUtilization({ siteId, timeRange }: { siteId?: string; timeRange: TimeRange }) {
  const { from, to } = useValidatedDateRange(timeRange)
  const { data, ...result } = useGetDepotUtilizationQuery(
    {
      depotId: siteId ?? '',
      from: from.time,
      to: to.time,
      intervalPeriod: '15m',
      measureNames: ['totalEnergyImport', 'totalEnergyExport']
    },
    { skip: !to.valid || !from.valid || !siteId }
  )

  return { energyUtilization: data, ...result }
}

/******************************************************************************
 * Helpers
 ******************************************************************************/
function useForecastRange(timeRange: TimeRange) {
  const { from, to } = useValidatedDateRange(timeRange)
  const now = useNowMemo()

  // Use the later of (now, from) as the start of the forecast range.
  const fromValidated = useMemo<ValidatedTime>(() => {
    if (!from.valid) return from

    if (now.isBefore(from.time)) {
      return { valid: true, time: from.time }
    } else {
      return { valid: true, time: now.toISOString() }
    }
  }, [from, now])

  // Use the later of (now, to) as the end of the forecast range.
  const toValidated = useMemo<ValidatedTime>(() => {
    if (!to.valid) return to

    if (now.isBefore(to.time)) {
      return { valid: true, time: to.time }
    } else {
      return { valid: true, time: now.toISOString() }
    }
  }, [to, now])

  return { from: fromValidated, to: toValidated }
}

type EventCategory = 'unfinished' | 'active' | 'nextDay' | 'nextWeek'

function useCategorizedEvents<T>(events: T[] | undefined, boundaryFn: (event: T) => Tuple<dayjs.ConfigType>) {
  const now = useNowMemo((d) => d)
  return useMemo(() => {
    const categories = { all: [], unfinished: [], active: [], nextDay: [], nextWeek: [] } as Record<EventCategory, T[]>

    events?.forEach((event) => {
      const eventCategories: EventCategory[] = []

      const [start, end] = boundaryFn(event)
      const unfinished = now.isBefore(end)
      const oneDayLater = now.add(1, 'day')
      const oneWeekLater = now.add(1, 'week')

      if (unfinished) {
        eventCategories.push('unfinished')
        if (now.isAfter(start)) {
          eventCategories.push('active')
        } else {
          if (oneDayLater.isAfter(start)) eventCategories.push('nextDay')
          if (oneWeekLater.isAfter(start)) eventCategories.push('nextWeek')
        }
      }

      eventCategories.forEach((category) => categories[category].push(event))
    })

    return categories
  }, [events, now, boundaryFn])
}
